import {
    ChangeDetectionStrategy,
    Component,
    Input,
    input,
    model,
    OnChanges,
    output,
    signal,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { ReportBuilder } from "./s25.document.report.builder.table.component";
import { Doc } from "./s25.document.const";
import { Document, DocumentService } from "../../../services/document.service";
import { ReportBuilderUtil } from "./s25.report.builder.util";

@Component({
    standalone: false,
    selector: "s25-ng-document-report-builder",
    template: `
        @if (!init()) {
            <s25-ng-loading-inline-static />
        } @else {
            <label class="reportBuilderLabel">Table Headers</label>
            @if (reportBuilder().header?.length) {
                <s25-ng-document-report-builder-table
                    class="header"
                    [type]="type"
                    [(rows)]="reportBuilder().header"
                    [section]="$any('header')"
                    [breakpoints]="breakpoints.body"
                    [allowEmpty]="true"
                    [editable]="editable()"
                    (changed)="onChange()"
                />
            } @else if (editable()) {
                <s25-ng-button class="addHeaders" [type]="'outline'" (click)="addHeaders()">Add Headers</s25-ng-button>
            } @else {
                <p class="addHeaders">None</p>
            }

            <label class="reportBuilderLabel body-label">Table Body</label>
            <s25-ng-document-report-builder-table
                class="body"
                [type]="type"
                [(rows)]="reportBuilder().body"
                [section]="$any('body')"
                [breakpoints]="breakpoints.header"
                [editable]="editable()"
                (changed)="onChange()"
            />
        }
    `,
    styles: `
        :host {
            --border-width: 1px;
            --border-color: #0002;
        }

        .addHeaders {
            display: block;
        }

        .body-label {
            margin-top: 1em;
        }

        :host.nested label,
        :host.nested s25-ng-button {
            display: none;
        }

        :host-context(s25-ng-document-report-builder-table) {
            .reportBuilderLabel,
            .addHeaders {
                display: none;
            }

            s25-ng-document-report-builder-table.header {
                border-bottom: var(--border-width) solid var(--border-color);
                display: block;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentReportBuilderComponent implements OnChanges {
    // Provide either document or document ID
    @Input() document: Document;
    @Input() documentId: number;

    @Input() type: Doc.ReportBuilderTypeId;
    editable = input<boolean>(true);
    reportBuilder = model<ReportBuilder>({
        variable: "ws.flexTable.reservations",
        header: [{ cells: new Array(4).fill(null).map((_) => ReportBuilderUtil.newCell({ section: "header" })) }],
        body: [{ cells: new Array(4).fill(null).map((_) => ReportBuilderUtil.newCell({ section: "body" })) }],
    });

    tableStrChange = output<string>();

    init = signal(false);
    tableStr: string = "";
    breakpoints: { header: number[]; body: number[] } = { header: [], body: [] };

    ngOnChanges(changes: SimpleChanges) {
        if (changes.document || changes.documentId) this.refresh();
        if (changes.type) this.onTypeChange();
    }

    async refresh() {
        this.init.set(false);
        let document: Document = this.document;
        if (!document) {
            document = await DocumentService.getDocument(this.documentId);
        }
        this.tableStr = document.body_text;
        if (!this.type) this.type = document.document_type as Doc.ReportBuilderTypeId;
        if (this.tableStr) this.reportBuilder.set(await ReportBuilderUtil.parse(this.tableStr, this.type));
        else this.reportBuilder().variable = `ws.flexTable.${this.type}`;
        this.init.set(true);
        this.onChange();
    }

    onChange() {
        this.breakpoints.header = ReportBuilderUtil.getBreakpoints(this.reportBuilder().header);
        this.breakpoints.body = ReportBuilderUtil.getBreakpoints(this.reportBuilder().body);
        this.stringify();
    }

    stringify() {
        this.tableStr = ReportBuilderUtil.stringify(this.reportBuilder(), this.type);
        this.tableStrChange.emit(this.tableStr);
    }

    addHeaders() {
        this.reportBuilder().header = [
            {
                cells: this.reportBuilder().body[0].cells.map((cell) =>
                    ReportBuilderUtil.newCell({
                        section: "header",
                        width: cell.width(),
                    }),
                ),
            },
        ];
        this.onChange();
    }

    onTypeChange() {
        // Clear variables
        for (const row of this.reportBuilder().header.concat(this.reportBuilder().body)) {
            for (const cell of row.cells) ReportBuilderUtil.clearVariable(cell);
        }
        this.stringify();
    }

    validate() {
        const rows = this.reportBuilder().header.concat(this.reportBuilder().body);
        return rows.every((row) => {
            return row.cells.every((cell) => cell.valid());
        });
    }
}
